import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { dataApi } from 'config/axios';

/** Dispatch this after updating the AI summary flag so all consumers refetch. */
export const AI_FEATURE_FLAG_INVALIDATED = 'ai-feature-flag-invalidated';

export async function checkAIFeatureFlag() {
    try {
        const response = await dataApi.get('/api/dynamic-reports/ai-enabled');
        return {
            summaryFlag: response?.data?.aiSummaryFeatureFlag,
            aiSummaryEnabled: response?.data?.aiSummaryEnabled === true,
            chatFlag: response?.data?.aiChatFeatureFlag,
            aiChatEnabled: response?.data?.aiChatEnabled === true,
            aiSurveyFeatureFlag: response?.data?.aiSurveyFeatureFlag,
            aiSurveyEnabled: response?.data?.aiSurveyEnabled === true,
        };
    } catch (error) {
        console.error('Error checking AI feature flag:', error);
        return false;
    }
}

function useFetchFlagOnce() {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            setLoading(true);
            const result = await checkAIFeatureFlag();
            if (isMounted) {
                setEnabled(result !== false && result?.aiSummaryEnabled === true);
                setLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    return { enabled, loading };
}

function useAIFeatureFlagCore(options = {}) {
    const { refetchDeps = [], refetchOnSchoolChange = false } = options;
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const schoolId = useSelector((state) => state.auth?.user?.schoolId);
    const [enabled, setEnabled] = useState(undefined);
    const [featureFlag, setFeatureFlag] = useState(false);
    const [aiChatEnabled, setAiChatEnabled] = useState(false);
    const [chatFlag, setChatFlag] = useState(false);
    const [aiSurveyEnabled, setAiSurveyEnabled] = useState(false);
    const [aiSurveyFeatureFlag, setAiSurveyFeatureFlag] = useState(false);
    const [loading, setLoading] = useState(true);

    const deps = refetchOnSchoolChange ? [refetchTrigger, schoolId] : [refetchTrigger, ...refetchDeps];

    useEffect(() => {
        let isMounted = true;

        const fetchFlag = async () => {
            setLoading(true);
            const result = await checkAIFeatureFlag();
            if (isMounted) {
                setEnabled(result === false ? false : result?.aiSummaryEnabled);
                setFeatureFlag(result !== false && result?.summaryFlag !== undefined ? result.summaryFlag : false);
                setAiChatEnabled(result !== false && result?.aiChatEnabled === true);
                setChatFlag(result !== false ? result?.chatFlag : false);
                setAiSurveyEnabled(result !== false && result?.aiSurveyEnabled === true);
                setAiSurveyFeatureFlag(result !== false ? result?.aiSurveyFeatureFlag : false);
                setLoading(false);
            }
        };

        fetchFlag();

        return () => {
            isMounted = false;
        };
    }, deps);

    const refetch = useCallback(() => {
        setRefetchTrigger((t) => t + 1);
    }, []);

    // When any consumer updates the flag, apply new value immediately and refetch so cards/summaries show or hide
    useEffect(() => {
        const onInvalidated = (event) => {
            if (event.detail?.enabled !== undefined) setEnabled(event.detail.enabled);
            setRefetchTrigger((t) => t + 1);
        };
        window.addEventListener(AI_FEATURE_FLAG_INVALIDATED, onInvalidated);
        return () => window.removeEventListener(AI_FEATURE_FLAG_INVALIDATED, onInvalidated);
    }, []);

    return {
        enabled,
        featureFlag,
        loading,
        refetch,
        aiChatEnabled,
        chatFlag,
        aiSurveyEnabled,
        aiSurveyFeatureFlag,
    };
}

export function useAIReportFeatureFlag() {
    return useFetchFlagOnce();
}

export function useAIChatFeatureFlag() {
    const { aiChatEnabled, chatFlag, loading } = useAIFeatureFlagCore();
    return { enabled: aiChatEnabled ?? false, chatFlag, loading };
}

export function useAISummaryFeatureFlag() {
    const { enabled, featureFlag, loading } = useAIFeatureFlagCore();
    return { enabled, featureFlag, loading };
}

export function useAISurveySummaryFeatureFlag() {
    const { aiSurveyEnabled, loading } = useAIFeatureFlagCore({ refetchOnSchoolChange: true });
    return { enabled: aiSurveyEnabled ?? false, loading };
}

const updateFeatureFlag = async (newValue) => {
    try {
        const response = await dataApi.post('/api/dynamic-reports/ai-enabled', {
            featureFlag: newValue,
        });
        // Success = request succeeded (backend returns featureFlag: true or false; both are valid)
        return response?.data?.success === true || response?.data?.featureFlag !== undefined;
    } catch (error) {
        console.error('Error updating AI summary feature flag (School Level):', error);
        return false;
    }
};

export function useAISummaryEnabledFlag({ mode = 'fetch' } = {}) {
    const { enabled, featureFlag, loading, refetch } = useAIFeatureFlagCore();
    const [updating, setUpdating] = useState(false);

    const updateFlagFn =
        mode === 'update'
            ? async (newValue) => {
                  setUpdating(true);
                  const success = await updateFeatureFlag(newValue);
                  if (success) {
                      const newEnabled = newValue?.ai_summary_enabled === true;
                      refetch();
                      window.dispatchEvent(
                          new CustomEvent(AI_FEATURE_FLAG_INVALIDATED, { detail: { enabled: newEnabled } }),
                      );
                  }
                  setUpdating(false);
                  return success;
              }
            : undefined;

    return { enabled, featureFlag, loading, updateFlag: updateFlagFn };
}

export function useUpdatingAISummaryFeatureFlag() {
    return useAISummaryEnabledFlag({ mode: 'update' });
}
